import { createApi } from '@reduxjs/toolkit/query/react';
import { buildBaseQuery } from 'app/api';
import {
  Tool,
  ToolMaintenance,
  ToolTest,
  CreateToolRequest,
  UpdateToolRequest,
  CreateToolMaintenanceRequest,
  UpdateToolMaintenanceRequest,
  CreateToolTestRequest,
  UpdateToolTestRequest,
  ProjectTool,
  ToolProject,
  ToolPublicOverview,
} from 'app/types/tool';
import { CodeValidation } from 'app/types/validation';

const name = 'toolsApi';

const tags = {
  TOOL_LIST: 'ToolList',
  TOOL: 'Tool',
  MAINTENANCE: 'ToolMaintenance',
  TEST: 'ToolTest',
  PROJECT_TOOL: 'ProjectTool',
  CATEGORIES: 'ToolCategories',
  MAKES: 'ToolMakes',
  MODELS: 'ToolModels',
  CERTIFICATE: 'ToolCertificate',
  TEST_REPORT: 'ToolTestReport',
  TOOL_PROJECTS: 'ToolProjects'
};

export const toolsApi = createApi({
  reducerPath: name,
  baseQuery: buildBaseQuery('api/tools'),
  tagTypes: Object.values(tags),
  endpoints: (builder) => ({
    // ===== TOOLS =====
    getTools: builder.query<Tool[], { unitId: number; isActive?: boolean }>({
      query: ({ unitId, isActive }) => {
        let url = `/${unitId}`;
        if (isActive !== undefined) {
          url += `?isActive=${isActive}`;
        }
        return url;
      },
      providesTags: [ tags.TOOL_LIST ],
    }),
    getCategories: builder.query<string[], number>({
      query: (unitId) => `/${unitId}/categories`,
      providesTags: [ tags.CATEGORIES ],
    }),
    getMakes: builder.query<string[], number>({
      query: (unitId) => `/${unitId}/makes`,
      providesTags: [ tags.MAKES ],
    }),
    getModels: builder.query<string[], number>({
      query: (unitId) => `/${unitId}/models`,
      providesTags: [ tags.MODELS ],
    }),
    createTool: builder.mutation<Tool, CreateToolRequest>({
      query: (body) => ({
        url: '/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [ tags.TOOL_LIST, tags.CATEGORIES, tags.MAKES, tags.MODELS ],
    }),
    updateTool: builder.mutation<Tool, UpdateToolRequest>({
      query: (body) => ({
        url: '/update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [ tags.TOOL_LIST, tags.TOOL ],
    }),
    uploadToolCertificate: builder.mutation<Tool, { toolId: number; file: File }>({
      query: ({ toolId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/${toolId}/upload/certificate`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [ tags.TOOL_LIST, tags.TOOL, tags.CERTIFICATE ],
    }),
    getToolById: builder.query<Tool, number>({
      query: (toolId) => `/${toolId}/detail`,
      providesTags: [ tags.TOOL ],
    }),
    deleteTool: builder.mutation<void, number>({
      query: (toolId) => ({
        url: `/${toolId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ tags.TOOL_LIST ],
    }),
    getToolIdByQrCode: builder.query<number | null, string>({
      query: (qrCode) => `/qrCode?code=${encodeURIComponent(qrCode)}`,
    }),
    isToolQrCodeAvailable: builder.query<boolean, CodeValidation>({
      query: (toolQrCodeValidation) => {
        let query = `/validate/qrCode?qrCode=${toolQrCodeValidation.code}`;
        if (toolQrCodeValidation.id) query += `&toolId=${toolQrCodeValidation.id}`;
        return query;
      },
      transformResponse: (response: any) => response.Result,
    }),
    isToolSerialNumberAvailable: builder.query<boolean, CodeValidation>({
      query: (serialNumberValidation) => {
        let query = `/validate/serialNumber?serialNumber=${encodeURIComponent(serialNumberValidation.code)}`;
        if (serialNumberValidation.id) query += `&toolId=${serialNumberValidation.id}`;
        return query;
      },
      transformResponse: (response: any) => response.Result,
    }),
    generateQrCodes: builder.mutation<string[], number>({
      query: (count) => ({
        url: '/qrCode/generate',
        method: 'POST',
        body: { count },
      }),
      transformResponse: (response: any) => response.Codes,
    }),
    getToolOverviewByBarcode: builder.query<ToolPublicOverview, string>({
      query: (barcode) => `/overview/${encodeURIComponent(barcode)}`,
    }),

    // ===== MAINTENANCE =====
    getMaintenance: builder.query<ToolMaintenance[], number>({
      query: (toolId) => `/${toolId}/maintenance`,
      providesTags: [ tags.MAINTENANCE ],
    }),
    createMaintenance: builder.mutation<ToolMaintenance, CreateToolMaintenanceRequest>({
      query: (body) => ({
        url: '/maintenance/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [ tags.MAINTENANCE, tags.TOOL, tags.TOOL_LIST ],
    }),
    updateMaintenance: builder.mutation<ToolMaintenance, UpdateToolMaintenanceRequest>({
      query: (body) => ({
        url: '/maintenance/update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [ tags.MAINTENANCE, tags.TOOL, tags.TOOL_LIST ],
    }),
    deleteToolMaintenance: builder.mutation<void, number>({
      query: (maintenanceId) => ({
        url: `/maintenance/${maintenanceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ tags.MAINTENANCE, tags.TOOL, tags.TOOL_LIST ],
    }),

    // ===== TESTS =====
    getTests: builder.query<ToolTest[], number>({
      query: (toolId) => `/${toolId}/tests`,
      providesTags: [ tags.TEST ],
    }),
    createTest: builder.mutation<ToolTest, CreateToolTestRequest>({
      query: (body) => ({
        url: '/tests/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [ tags.TEST, tags.TOOL ],
    }),
    updateTest: builder.mutation<ToolTest, UpdateToolTestRequest>({
      query: (body) => ({
        url: '/tests/update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [ tags.TEST, tags.TOOL, tags.TOOL_LIST ],
    }),
    uploadTestReport: builder.mutation<ToolTest, { testId: number; file: File }>({
      query: ({ testId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/tests/${testId}/upload/report`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [ tags.TEST ],
    }),
    deleteToolTest: builder.mutation<void, number>({
      query: (testId) => ({
        url: `/tests/${testId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ tags.TEST, tags.TOOL, tags.TOOL_LIST ],
    }),

    // ===== PROJECT =====
    getProjectTools: builder.query<ProjectTool[], number>({
      query: (projectId) => `/project/${projectId}`,
      providesTags: [ tags.PROJECT_TOOL ],
    }),
    getAvailableToolsForProject: builder.query<Tool[], { projectId: number }>({
      query: ({ projectId }) => {
        const url = `/project/${projectId}/available`;
        return url;
      },
    }),
    linkToolToProject: builder.mutation<ProjectTool, { projectId: number, body: { toolIds: number[] } }>({
      query: (values) => ({
        url: `/project/${values.projectId}`,
        method: 'POST',
        body: values.body,
      }),
      invalidatesTags: [ tags.PROJECT_TOOL ],
    }),
    getToolProjects: builder.query<ToolProject[], number>({
      query: (toolId) => `/${toolId}/projects`,
      providesTags: [ tags.TOOL_PROJECTS ],
    }),
    deleteProjectTool: builder.mutation<void, number>({
      query: (projectToolId) => ({
        url: `/projectTool/${projectToolId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ tags.PROJECT_TOOL, tags.TOOL_PROJECTS ],
    }),
  }),
});

export const {
  useGetToolsQuery,
  useLazyGetToolsQuery,
  useLazyGetCategoriesQuery,
  useLazyGetMakesQuery,
  useLazyGetModelsQuery,
  useCreateToolMutation,
  useUpdateToolMutation,
  useUploadToolCertificateMutation,
  useGetToolByIdQuery,
  useDeleteToolMutation,
  useLazyGetToolIdByQrCodeQuery,
  useLazyIsToolQrCodeAvailableQuery,
  useLazyIsToolSerialNumberAvailableQuery,
  useGenerateQrCodesMutation,
  useGetToolOverviewByBarcodeQuery,
  useLazyGetToolOverviewByBarcodeQuery,

  useGetMaintenanceQuery,
  useCreateMaintenanceMutation,
  useUpdateMaintenanceMutation,
  useDeleteToolMaintenanceMutation,

  useGetTestsQuery,
  useCreateTestMutation,
  useUpdateTestMutation,
  useUploadTestReportMutation,
  useDeleteToolTestMutation,

  useGetProjectToolsQuery,
  useGetAvailableToolsForProjectQuery,
  useLazyGetAvailableToolsForProjectQuery,
  useLinkToolToProjectMutation,
  useGetToolProjectsQuery,
  useDeleteProjectToolMutation,

} = toolsApi;
